import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox, Form, Input } from 'antd';

import FormLabel from 'components/FormLabel';
import LicenseTag from 'components/LicenseTag';
import PhoneInput, { validPhoneNumber } from 'components/PhoneInput';
import useFormatMessage from 'hooks/useFormatMessage';
import messages from './messages';

const CustomerUserForm = ({ form, onSubmit, customerAccess = {}, customerRoles = [], dataSourceRoles = [], dataSources, dataSourceAdvancedUserRoleId }) => {
  useEffect(() => form.resetFields(), [customerAccess]);
  const $t = useFormatMessage();

  const { user, roles } = customerAccess;
  const roleOpts = customerRoles.map((role) => ({ label: role.name, value: role.id }));

  const getUsers = (dataSource, userId) => dataSource.access?.filter(wa => wa.customerAccess.user.id !== userId)?.length || 0;
  const getAdvancedUsers = (dataSource, userId) => dataSource.access?.filter(wa => wa.customerAccess.user.id !== userId && wa.roles.some(r => r.id === dataSourceAdvancedUserRoleId))?.length || 0;

  const getWebsiteRoleOpts = (dataSource) => dataSourceRoles.map((role) => {
    let disabled = false;
    let extra = null;

    if (role.id === dataSourceAdvancedUserRoleId && dataSource.activeLicense) {
      const { advancedUserLimit } = dataSource.activeLicense.settings;
      if (advancedUserLimit > 0) {
        const advancedUsers = getAdvancedUsers(dataSource);
        const otherAdvancedUsers = getAdvancedUsers(dataSource, user?.id);
        disabled = otherAdvancedUsers >= advancedUserLimit && otherAdvancedUsers === advancedUsers;
        extra = ` (${advancedUsers}/${advancedUserLimit}) `;
      }
    }

    return {
      label: (
        <>
          {role.name}
          {extra}
          {disabled && <LicenseTag dataSourceId={dataSource.id} setting="advancedUserLimit" showTooltip />}
        </>
      ),
      value: role.id,
      disabled,
    };
  });

  const getDataSourceGroupProps = (dataSource) => {
    let label = dataSource.title;
    let disabled = false;
    if (dataSource.activeLicense) {
      const { userLimit } = dataSource.activeLicense.settings;

      if (userLimit > 0) {
        const users = getUsers(dataSource);
        const otherUsers = getUsers(dataSource, user?.id);
        disabled = otherUsers >= userLimit && otherUsers === users;
        label = (
          <>
            {dataSource.title}
            {` (${users}/${userLimit}) `}
            {disabled && <LicenseTag dataSourceId={dataSource.id} setting="userLimit" showTooltip />}
          </>
        );
      }
    }

    return {
      label: <h3>{label}</h3>,
      disabled,
    };
  };

  const initialValues = {
    email: user?.email,
    firstname: user?.firstname,
    lastname: user?.lastname,
    phone: user?.phone,
    roleIds: roles?.map((role) => role.id) || [],
    dataSourceAccess: customerAccess?.dataSourceAccess?.reduce((obj, access) => {
      obj[access.dataSource.id] = {
        roleIds: access?.roles?.map(r => r.id) || [],
      };
      return obj;
    }, {}),
  };

  const onFinish = ({ dataSourceAccess, ...rest }) => {
    onSubmit({
      ...rest,
      dataSourceAccess: dataSourceAccess && Object.keys(dataSourceAccess).filter(id => dataSourceAccess[id].roleIds?.length).map(dataSourceId => ({
        dataSourceId,
        ...dataSourceAccess[dataSourceId],
      })),
    });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
    >
      <Form.Item
        name="email"
        label={<FormLabel label={messages.email} />}
      >
        <Input disabled={!!user} />
      </Form.Item>
      {user && (
        <>
          <Form.Item
            name="firstname"
            label={<FormLabel label={messages.firstname} />}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastname"
            label={<FormLabel label={messages.lastname} />}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label={<FormLabel label={messages.phone} />}
            rules={[validPhoneNumber]}
          >
            <PhoneInput />
          </Form.Item>
        </>
      )}
      <h3>Roles</h3>
      <p>
        User roles are limited to your license. There are two kinds: The Account role and the Data source role.
        {' '}
        <a target="_blank" href="https://academy.extellio.com/knowledge-base/get-started/understanding-the-different-user-permissions">Read more about our roles in our guide</a>
      </p>
      <h3><FormattedMessage {...messages.authRoles} /></h3>
      <Form.Item
        name="roleIds"
        rules={[{ required: true, message: $t(messages.authRolesRequired) }]}
      >
        <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '.5em' }} options={roleOpts} />
      </Form.Item>
      <h3><FormattedMessage {...messages.dataSourceRoles} /></h3>
      <p>The roles listed below are individual to each data source.</p>
      {dataSources.map((dataSource) => {
        const { label, disabled } = getDataSourceGroupProps(dataSource);

        return (
          <Form.Item key={dataSource.id} name={['dataSourceAccess', dataSource.id, 'roleIds']} label={label}>
            <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '.5em' }} options={getWebsiteRoleOpts(dataSource)} disabled={disabled} />
          </Form.Item>
        );
      })}
    </Form>
  );
};

export default CustomerUserForm;
